import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Play, Pause, RotateCcw, ChevronRight, ArrowRight, ArrowUp, ArrowDown,
  Sparkles, Loader2, Radio, Boxes, Rocket, Cpu, RefreshCw, Recycle,
  Send, CheckCircle2, FileText, Quote, Zap,
} from "lucide-react";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DocViewer } from "@/components/DocViewer";
import { DexDeviceRow } from "@/components/DexDeviceRow";
import { ProbabilisticPanel, BASELINE_SLIDERS } from "@/components/ProbabilisticPanel";
import { AgentPhaseFlow } from "@/components/AgentPhaseFlow";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// Agent Console — live demo view for all six Flexspace lifecycle agents.
//
// Replaces the static Lifecycle timeline and the Storyline modal.
// Four-panel workbench per agent: Inbox · Reasoning · Decision · Outbox.
// Backed by /api/lifecycle/story (each hop carries a `console` payload).
// -----------------------------------------------------------------------------

const AGENT_ICONS = {
  plan:    Radio,
  source:  Boxes,
  deploy:  Rocket,
  operate: Cpu,
  refresh: RefreshCw,
  recover: Recycle,
};

const AGENT_QUESTIONS = {
  plan:    "What do we really need?",
  source:  "How should we source it?",
  deploy:  "Will everyone be ready?",
  operate: "What is becoming risky?",
  refresh: "What action per device?",
  recover: "What value can we retain?",
};

// Which inbox tiles are editable per agent (indices into the console.inbox array).
// The presenter can tweak these numbers during Idle state to answer live what-if
// questions from execs; the agent's decision total recomputes on the next Run.
// Kept scoped to Demand for MVP — cascading edits into downstream agents would
// drift the numbers used on Blueprint / Finance / Cockpit.
const EDITABLE_INBOX = {
  plan: [0, 1, 2, 3], // Workday joiners · SN HAM aging · Nexthink retained · Finance exclusions
};

// Which real-looking documents each agent produces (drives the Outbox chips).
const DOCS_BY_STAGE = {
  source:  ["ariba_pr", "oem_po"],
  deploy:  ["shipment_manifest", "provisioning_task"],
  recover: ["wipe_certificate", "itad_grading"],
};

// Timing (ms). Multiplied by 1/speed at runtime.
const T_INBOX_TICK    = 550;
const T_REASONING_STEP = 900;
const T_DECISION_TERM = 500;
const T_OUTBOX_FIELD  = 350;
const T_HANDOFF_HOLD  = 900;

const STATUS_LABELS = {
  idle:    "Idle",
  running: "Running",
  ready:   "Ready",
  passed:  "Complete",
};

// ---------- utility: sleep respects speed multiplier ----------
function makeSleep(getSpeed, pausedRef) {
  return async (ms) => {
    const scaled = ms / getSpeed();
    const step = 40;
    let elapsed = 0;
    while (elapsed < scaled) {
      // wait while paused
      while (pausedRef.current) await new Promise((r) => setTimeout(r, 60));
      await new Promise((r) => setTimeout(r, step));
      elapsed += step;
    }
  };
}

// ==================== Root Page ====================
export default function Lifecycle() {
  const { brand } = useBranding();
  const [params] = useSearchParams();
  const autoplayOnLoad = params.get("autoplay") === "1";
  const initialStage = params.get("stage");

  const [story, setStory] = useState(null);
  // Default to the first hop in the story's order (set when the fetch resolves)
  // so the Agent Console always lands on whichever agent the backend puts first.
  const [activeStage, setActiveStage] = useState(initialStage || null);
  const [speed, setSpeed] = useState(1);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // Per-agent runtime state: status, revealed counts + committed context.
  const [state, setState] = useState(() => makeInitialState());
  const runningRef = useRef(false);

  // Persistent Lifecycle Context (accumulated across agents).
  const [context, setContext] = useState({});

  // What-if overrides: per-stage per-term-index → number. Populated when the
  // presenter tweaks an editable inbox tile during Idle. Cleared on Reset.
  const [overrides, setOverrides] = useState({});
  const setOverride = (stage, termIdx, value) => {
    setOverrides((o) => ({ ...o, [stage]: { ...(o[stage] || {}), [termIdx]: value } }));
  };
  const clearOverrides = (stage) => {
    setOverrides((o) => { const next = { ...o }; delete next[stage]; return next; });
  };

  // Document viewer (modal) — opened from the Outbox "Show docs" chip.
  const [docViewer, setDocViewer] = useState({ open: false, ids: [], initial: null });
  const openDocsFor = (stage) => {
    const ids = DOCS_BY_STAGE[stage] || [];
    if (!ids.length) return;
    setDocViewer({ open: true, ids, initial: ids[0] });
  };

  // Shared probabilistic slider state (cascades across Demand / Refresh /
  // Recovery agents). Initialised to the baseline that lands P50 on the
  // canonical story numbers (186 / 186 / 429).
  const [probSliders, setProbSliders] = useState(() => ({ ...BASELINE_SLIDERS }));
  const resetProbSliders = () => setProbSliders({ ...BASELINE_SLIDERS });
  // Latest forecast per stage — populated by ProbabilisticPanel.onForecastUpdate.
  // Shared with AgentWorkbench so the Decision panel visibly tracks the
  // probabilistic P50 instead of showing an unrelated number.
  const [probForecast, setProbForecast] = useState({ plan: null, refresh: null, recover: null });
  const updateForecast = (stage, fc) => setProbForecast((p) => ({ ...p, [stage]: fc }));

  useEffect(() => {
    endpoints.lifecycleStory().then((s) => {
      setStory(s);
      // Once the story loads, if no stage was requested via ?stage=…
      // start on the first hop so the console respects the backend's order.
      setActiveStage((prev) => prev || s?.hops?.[0]?.stage || "operate");
    }).catch(() => toast.error("Could not load lifecycle story."));
  }, []);

  // Auto-play once when arrived with ?autoplay=1
  useEffect(() => {
    if (autoplayOnLoad && story && !runningRef.current) {
      runFullLifecycle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story, autoplayOnLoad]);

  const getSpeed = () => speed;
  const sleep = useMemo(() => makeSleep(getSpeed, pausedRef), [speed]);

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  const activeHop = useMemo(
    () => story?.hops?.find((h) => h.stage === activeStage),
    [story, activeStage]
  );

  // ---------------- Run: single agent ----------------
  // Stages that have a Scenario Planner (Plan step). For these, Run agent is
  // split into two user triggers: "Connect sources" fires Phase 1 (inbox) and
  // "Run agent" (in the Plan step) fires Phases 2–5 (reasoning → handoff).
  const PLAN_STAGES = new Set(["plan", "refresh", "recover"]);

  // Phase 1 only — the inbox / connector pulls. Used by both runAgent and the
  // standalone "Connect sources" button inside the Collect step.
  const runCollectPhase = async (stage) => {
    if (runningRef.current) return;
    runningRef.current = true;
    const hop = story?.hops?.find((h) => h.stage === stage);
    if (!hop) { runningRef.current = false; return; }
    const c = hop.console || {};
    setActiveStage(stage);
    setState((s) => ({ ...s, [stage]: { ...emptyAgent(), status: "running" } }));
    for (let i = 0; i < (c.inbox?.length || 0); i++) {
      await sleep(T_INBOX_TICK);
      setState((s) => ({ ...s, [stage]: { ...s[stage], inboxRevealed: i + 1 } }));
    }
    // Mark collect done. For plan-equipped stages, we PAUSE here and wait
    // for the user to click "Run agent" in the Plan step. For other stages,
    // runAgent will call runReasoningPhase immediately after.
    setState((s) => ({ ...s, [stage]: { ...s[stage], collectReady: true } }));
    runningRef.current = false;
  };

  // Phase 2–5 — reasoning → decision → handoff. Can only run after Phase 1.
  const runReasoningPhase = async (stage) => {
    if (runningRef.current) return;
    runningRef.current = true;
    const hop = story?.hops?.find((h) => h.stage === stage);
    if (!hop) { runningRef.current = false; return; }
    const c = hop.console || {};
    setActiveStage(stage);
    setState((s) => ({ ...s, [stage]: { ...s[stage], status: "running" } }));
    for (let i = 0; i < (c.reasoning?.length || 0); i++) {
      await sleep(T_REASONING_STEP);
      setState((s) => ({ ...s, [stage]: { ...s[stage], reasoningRevealed: i + 1 } }));
    }
    for (let i = 0; i < (c.decision?.terms?.length || 0); i++) {
      await sleep(T_DECISION_TERM);
      setState((s) => ({ ...s, [stage]: { ...s[stage], decisionTermsRevealed: i + 1 } }));
    }
    await sleep(T_DECISION_TERM);
    setState((s) => ({ ...s, [stage]: { ...s[stage], decisionSettled: true } }));
    for (let i = 0; i < (c.handoff?.fields?.length || 0); i++) {
      await sleep(T_OUTBOX_FIELD);
      setState((s) => ({ ...s, [stage]: { ...s[stage], outboxRevealed: i + 1 } }));
    }
    setState((s) => ({ ...s, [stage]: { ...s[stage], status: "ready" } }));
    runningRef.current = false;
  };

  const runAgent = async (stage) => {
    // Non-plan stages run end-to-end in one click. Plan stages only Phase 1
    // and wait for the user to click "Run agent" from the Plan step.
    await runCollectPhase(stage);
    if (!PLAN_STAGES.has(stage)) {
      await runReasoningPhase(stage);
    }
  };

  // Commit handoff -> next agent's inbox
  const sendHandoff = (stage) => {
    const hop = story?.hops?.find((h) => h.stage === stage);
    if (!hop) return;
    const c = hop.console || {};
    // Add BOTH flavours of context to the persistent Lifecycle Context dict:
    //   • handoff.fields  — raw payload the next agent will consume (cohort,
    //                       dex_risk, etc.)
    //   • context_added   — the curated, demo-facing summary keys the Hero
    //                       Band + Lifecycle Context strip actually render
    //                       (hotspot_cohort, refresh_decisions, …). Writing
    //                       them here is what makes the strip's chips light
    //                       up when the agent commits.
    setContext((prev) => {
      const next = { ...prev };
      (c.handoff?.fields || []).forEach((f) => { next[f.key] = f.value; });
      // Also commit the secondary (parallel) handoff when the agent emits
      // a dual payload (e.g. Refresh → Demand + Recovery).
      (c.handoff?.secondary?.fields || []).forEach((f) => { next[f.key] = f.value; });
      Object.entries(hop.context_added || {}).forEach(([k, v]) => { next[k] = v; });
      return next;
    });
    setState((s) => ({ ...s, [stage]: { ...s[stage], status: "passed", handoffSent: true } }));
    // move active to next
    const nextStage = c.handoff?.target;
    if (nextStage) setActiveStage(nextStage);
  };

  // ---------------- Run: full lifecycle ----------------
  const runFullLifecycle = async () => {
    if (runningRef.current) return;
    // reset all
    setState(makeInitialState());
    setContext({});
    for (const hop of story?.hops || []) {
      await runAgent(hop.stage);
      // auto-commit handoff (except closes_loop still commits but active stays)
      await sleep(T_HANDOFF_HOLD);
      sendHandoff(hop.stage);
      await sleep(T_HANDOFF_HOLD / 2);
    }
  };

  const reset = () => {
    if (runningRef.current) return;
    setState(makeInitialState());
    setContext({});
    setOverrides({});
    setActiveStage("plan");
  };

  if (!story) {
    return (
      <div className="max-w-[1240px] mx-auto py-24 text-center text-slate-500">
        <Loader2 size={20} className="mx-auto mb-3 animate-spin" style={{ color: brandBlue }} />
        Loading Agent Console…
      </div>
    );
  }

  return (
    <div className="max-w-[1240px] mx-auto" data-testid="agent-console-page">
      {/* ============ Header ============ */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-3" style={{ color: brandBlue }}>
            Agent Console · Lifecycle Intelligence
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-[1.15]">
            Watch <span style={{ color: brandBlue }}>Flexspace</span> run the lifecycle.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl text-[15px] leading-7">
            {story.scenario.opening_question}
          </p>
        </div>
        <RunControls
          brandBlue={brandBlue}
          brandSoft={brandSoft}
          onRunAgent={() => runAgent(activeStage)}
          onRunAll={runFullLifecycle}
          onReset={reset}
          paused={paused}
          onTogglePause={() => setPaused((p) => !p)}
          speed={speed}
          onSpeed={setSpeed}
        />
      </div>

      {/* ============ Story Hero Band ============ */}
      <StoryHeroBand story={story} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* ============ Persistent Lifecycle Context ============ */}
      <LifecycleContextStrip
        context={context}
        story={story}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />

      {/* ============ Six-agent rail ============ */}
      <AgentRail
        hops={story.hops}
        state={state}
        active={activeStage}
        onSelect={(s) => setActiveStage(s)}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />

      {/* ============ Console workbench (active agent) ============ */}
      {/* For Demand / Refresh / Recovery the ProbabilisticPanel is folded in */}
      {/* as the first step ("Plan · Scenario Planner") inside the workbench. */}
      {activeHop && (
        <AgentWorkbench
          key={activeHop.stage}
          hop={activeHop}
          agentState={state[activeHop.stage]}
          stageOverrides={overrides[activeHop.stage] || {}}
          onOverride={(idx, val) => setOverride(activeHop.stage, idx, val)}
          onClearOverrides={() => clearOverrides(activeHop.stage)}
          onSendHandoff={() => sendHandoff(activeHop.stage)}
          onRun={() => runAgent(activeHop.stage)}
          onConnectSources={() => runCollectPhase(activeHop.stage)}
          onRunReasoning={() => runReasoningPhase(activeHop.stage)}
          hasPlanPhase={PLAN_STAGES.has(activeHop.stage)}
          onOpenDocs={() => openDocsFor(activeHop.stage)}
          docCount={(DOCS_BY_STAGE[activeHop.stage] || []).length}
          forecastLink={probForecast[activeHop.stage]}
          planPanel={
            ["plan", "refresh", "recover"].includes(activeHop.stage) ? (
              <ProbabilisticPanel
                stage={activeHop.stage}
                sliders={probSliders}
                onSlidersChange={setProbSliders}
                onReset={resetProbSliders}
                onForecastUpdate={(fc) => updateForecast(activeHop.stage, fc)}
                agentRunning={state[activeHop.stage]?.status === "running"}
                agentSettled={state[activeHop.stage]?.decisionSettled}
                collectReady={state[activeHop.stage]?.collectReady}
                reasoningStarted={state[activeHop.stage]?.reasoningRevealed > 0 || state[activeHop.stage]?.decisionSettled}
                onRunReasoning={() => runReasoningPhase(activeHop.stage)}
                embedded
                brandBlue={brandBlue}
                brandSoft={brandSoft}
              />
            ) : null
          }
          brandBlue={brandBlue}
          brandSoft={brandSoft}
        />
      )}

      {/* Document viewer modal — real-looking demo artifacts */}
      <DocViewer
        open={docViewer.open}
        onOpenChange={(v) => setDocViewer((s) => ({ ...s, open: v }))}
        docIds={docViewer.ids}
        initialDocId={docViewer.initial}
      />
    </div>
  );
}
// ==================== Run Controls ====================
function RunControls({ brandBlue, brandSoft, onRunAgent, onRunAll, onReset, paused, onTogglePause, speed, onSpeed }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="run-controls">
      <Button
        data-testid="run-agent-btn"
        variant="outline"
        onClick={onRunAgent}
        className="h-10 px-4 border-slate-200"
      >
        <Play size={13} className="mr-1.5" style={{ color: brandBlue }} />
        Run agent
      </Button>
      <Button
        data-testid="run-full-lifecycle-btn"
        onClick={onRunAll}
        className="h-10 px-4 text-white hover:opacity-90"
        style={{ backgroundColor: brandBlue }}
      >
        <Sparkles size={13} className="mr-1.5" />
        Run full lifecycle
      </Button>
      <Button
        data-testid="pause-btn"
        variant="outline"
        onClick={onTogglePause}
        className="h-10 px-3 border-slate-200"
        title={paused ? "Resume" : "Pause"}
      >
        {paused ? <Play size={13} /> : <Pause size={13} />}
      </Button>
      <Button
        data-testid="reset-btn"
        variant="outline"
        onClick={onReset}
        className="h-10 px-3 border-slate-200"
        title="Reset"
      >
        <RotateCcw size={13} />
      </Button>
      <div className="h-6 w-px bg-slate-200 mx-1" />
      <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 p-0.5">
        {[0.5, 1, 2].map((s) => (
          <button
            key={s}
            data-testid={`speed-${s}x`}
            onClick={() => onSpeed(s)}
            className="text-xs font-semibold h-8 px-3 rounded-md transition-colors"
            style={
              speed === s
                ? { backgroundColor: brandBlue, color: "#fff" }
                : { color: "#64748B" }
            }
          >
            {s}x
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== Lifecycle Context Strip ====================
// Shows one headline chip per agent (plus the seed context) from the moment
// the page loads: slots start in a muted "pending" state (dashed border,
// grey, no value) and transition to a lit state with a 1.2s brand-blue glow
// the instant the producing agent commits its handoff. One chip per agent is
// intentional — the dict holds all 22+ context keys, but the strip is a
// scannable scoreboard, not a dump of every key/value.
const CONTEXT_HEADLINE_PER_STAGE = {
  operate: "hotspot_cohort",
  refresh: "refresh_decisions",
  plan:    "net_requirement",
  source:  "sourcing_plan",
  deploy:  "day1_ready",
  recover: "returns_pipeline_next_qtr",
};

function LifecycleContextStrip({ context, story, brandBlue, brandSoft }) {
  // Ordered slot list: seed context chips (one per seed key, pre-committed)
  // followed by one headline chip per agent, in the rail order the backend
  // serves. Each slot keeps the stage it comes from so a tooltip can read
  // "net_requirement · from Demand".
  const expected = useMemo(() => {
    const list = [];
    const label = (stage) => (story?.hops?.find((h) => h.stage === stage)?.name) || stage;
    Object.keys(story?.context_seed || {}).forEach((k) => {
      list.push({ key: k, producer: null, producerLabel: "seed" });
    });
    (story?.hops || []).forEach((h) => {
      const headline = CONTEXT_HEADLINE_PER_STAGE[h.stage];
      if (headline && (h.context_added || {})[headline] !== undefined) {
        list.push({ key: headline, producer: h.stage, producerLabel: label(h.stage) });
      }
    });
    return list;
  }, [story]);

  // Seed context is pre-committed from t=0, so merge it into the lit set
  // before checking individual chips.
  const seedKeys = useMemo(() => new Set(Object.keys(story?.context_seed || {})), [story]);
  const litMerged = { ...(story?.context_seed || {}), ...(context || {}) };

  // Track which keys got committed in the last render tick so we can briefly
  // flash each one. `committedAt` holds `{key: timestamp}` and chips lit in the
  // last 1200ms get the glow treatment.
  const prevKeysRef = useRef(new Set());
  const [committedAt, setCommittedAt] = useState({});
  useEffect(() => {
    const nowKeys = new Set(Object.keys(context || {}));
    const added = [];
    nowKeys.forEach((k) => { if (!prevKeysRef.current.has(k)) added.push(k); });
    prevKeysRef.current = nowKeys;
    if (added.length === 0) return;
    const t = Date.now();
    setCommittedAt((prev) => {
      const next = { ...prev };
      added.forEach((k) => { next[k] = t; });
      return next;
    });
    // After the glow window, drop the entry so subsequent renders see a static chip
    const timeout = setTimeout(() => {
      setCommittedAt((prev) => {
        const next = { ...prev };
        added.forEach((k) => {
          if (next[k] === t) delete next[k];
        });
        return next;
      });
    }, 1400);
    return () => clearTimeout(timeout);
  }, [context]);

  const anyCommitted = Object.keys(context || {}).length > 0;

  return (
    <div
      className="rounded-xl px-5 py-3 mb-6 flex items-start gap-3 flex-wrap"
      style={{ backgroundColor: brandSoft, border: `1px solid ${brandSoft}` }}
      data-testid="lifecycle-context-strip"
    >
      <div className="text-[10px] tracking-[0.14em] uppercase font-bold shrink-0 pt-1" style={{ color: brandBlue }}>
        Lifecycle Context
      </div>
      {expected.length === 0 ? (
        <div className="text-xs text-slate-500 pt-1">
          Empty · runs will populate context as each agent commits its handoff
        </div>
      ) : (
        <div className="flex items-start gap-2 flex-wrap min-w-0">
          {expected.map(({ key: k, producerLabel }) => {
            const isSeed = seedKeys.has(k);
            const lit    = isSeed || Object.prototype.hasOwnProperty.call(context || {}, k);
            const glow   = committedAt[k];
            const value  = lit ? String(litMerged[k]) : "pending";
            const shortVal = value.length > 46 ? value.slice(0, 46) + "…" : value;
            const chipStyle = lit
              ? {
                  color: "#0F172A",
                  backgroundColor: glow ? `${brandBlue}1A` : "rgba(255,255,255,0.7)",
                  border: `1px solid ${glow ? brandBlue : `${brandBlue}22`}`,
                  boxShadow: glow ? `0 0 0 3px ${brandBlue}22, 0 4px 10px ${brandBlue}33` : "none",
                  animation: glow ? "chipCommit 1200ms ease-out" : "none",
                }
              : {
                  color: "#94A3B8",
                  backgroundColor: "transparent",
                  border: `1px dashed #CBD5E1`,
                };
            return (
              <span
                key={k}
                className="text-[11px] font-medium rounded-md px-2 py-1 transition-all duration-300"
                style={chipStyle}
                data-testid={`context-key-${k}`}
                data-committed={lit ? "true" : "false"}
                title={`${k} · from ${producerLabel}`}
              >
                <span className="font-semibold uppercase tracking-wider text-[9px] mr-1" style={{ color: lit ? "#64748B" : "#94A3B8" }}>
                  {k.replace(/_/g, " ")}:
                </span>
                <span style={{ color: lit ? brandBlue : "#94A3B8", fontWeight: lit ? 600 : 400 }}>
                  {shortVal}
                </span>
              </span>
            );
          })}
          {!anyCommitted && (
            <span
              className="text-[10px] text-slate-400 ml-1 pt-1"
              data-testid="context-hint"
            >
              Run an agent to light its chips.
            </span>
          )}
        </div>
      )}
      <style>{`
        @keyframes chipCommit {
          0%   { transform: translateY(-2px) scale(0.98); opacity: 0.6; }
          40%  { transform: translateY(0)    scale(1.04); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ==================== Six-agent rail ====================
function AgentRail({ hops, state, active, onSelect, brandBlue, brandSoft }) {
  // Group the 6 agents into their asset-management lifecycle phase so execs
  // see where in the end-to-end journey each agent sits. Phases spans the
  // chip width directly beneath the rail.
  //   Operate (Device) → Plan/Forecast (Refresh, Demand) → Procure (Supply)
  //   → Deploy (Fulfilment) → Retire (Recovery) → closes back to Operate.
  const PHASE_FOR_STAGE = {
    operate: { id: "operate",  label: "Operate",       color: "#0EA5E9" },
    refresh: { id: "plan",     label: "Plan / Forecast", color: "#2563EB" },
    plan:    { id: "plan",     label: "Plan / Forecast", color: "#2563EB" },
    source:  { id: "procure",  label: "Procure / Source", color: "#8B5CF6" },
    deploy:  { id: "deploy",   label: "Deploy",         color: "#059669" },
    recover: { id: "retire",   label: "Retire / Recover", color: "#DB2777" },
  };
  // Collapse adjacent same-phase chips into one band segment that spans them.
  const segments = [];
  hops.forEach((h) => {
    const p = PHASE_FOR_STAGE[h.stage];
    if (segments.length && segments[segments.length - 1].id === p.id) {
      segments[segments.length - 1].count += 1;
    } else {
      segments.push({ ...p, count: 1 });
    }
  });

  return (
    <div className="mb-8" data-testid="agent-rail">
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {hops.map((h, i) => {
          const isActive = h.stage === active;
          const s = state[h.stage];
          const passed = s?.status === "passed";
          const running = s?.status === "running";
          const ready = s?.status === "ready";
          const Icon = AGENT_ICONS[h.stage] || Radio;
          return (
            <div key={h.stage} className="flex items-center shrink-0">
              <button
                data-testid={`agent-card-${h.stage}`}
                onClick={() => onSelect(h.stage)}
                className="text-left rounded-xl transition-all p-3 hover:bg-slate-50 min-w-[152px]"
                style={
                  isActive
                    ? { backgroundColor: brandSoft, border: `1px solid ${brandBlue}` }
                    : { border: `1px solid transparent` }
                }
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
                    style={
                      isActive || passed || ready
                        ? { backgroundColor: brandBlue, color: "#fff" }
                        : { border: "1.5px solid #E2E8F0", color: "#64748B" }
                    }
                  >
                    <Icon size={15} />
                  </div>
                  <StatusPill status={s?.status || "idle"} brandBlue={brandBlue} />
                </div>
                <div className="text-sm font-semibold" style={{ color: isActive ? brandBlue : "#0F172A" }}>
                  {h.name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-4">
                  {AGENT_QUESTIONS[h.stage]}
                </div>
              </button>
              {i < hops.length - 1 && (
                <div className="px-1.5" aria-hidden>
                  <div
                    className="w-6 h-px transition-colors"
                    style={{
                      backgroundColor: passed ? "#10B981" : "#CBD5E1",
                      transition: "background-color 400ms ease",
                    }}
                    data-testid={`rail-connector-${h.stage}`}
                  />
                </div>
              )}
              {/* Closed-loop indicator from Recovery back to the first hop */}
              {i === hops.length - 1 && state["recover"]?.status === "passed" && (
                <div className="px-2 hidden lg:flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#10B981" }}>
                  <div className="w-4 h-px" style={{ backgroundColor: "#10B981" }} />
                  next cycle
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lifecycle-phase band — groups adjacent chips by asset-management phase.
          Widths computed to exactly match the chip row structure above:
            chip min-w  = 152 px
            connector   =  36 px (px-1.5 + w-6)
            inter-cell  =   4 px (parent gap-1)
          Segment width = n_chips × 152 + (n_chips − 1) × 40 + (36 if not last). */}
      <div
        className="flex items-stretch gap-1 overflow-x-auto"
        data-testid="agent-phase-band"
        aria-label="Asset management lifecycle phases"
      >
        {segments.map((seg, idx) => {
          const isLastSeg = idx === segments.length - 1;
          const widthPx = seg.count * 152 + (seg.count - 1) * 40 + (isLastSeg ? 0 : 36);
          const isActivePhase =
            segments.slice(0, idx).reduce((n, s) => n + s.count, 0) <=
              hops.findIndex((h) => h.stage === active) &&
            hops.findIndex((h) => h.stage === active) <
              segments.slice(0, idx + 1).reduce((n, s) => n + s.count, 0);
          return (
            <div
              key={`${seg.id}-${idx}`}
              className="shrink-0 rounded-md transition-all flex items-center justify-center h-7 px-3"
              style={{
                width: `${widthPx}px`,
                backgroundColor: isActivePhase ? seg.color : `${seg.color}1A`,
                border: `1px solid ${seg.color}${isActivePhase ? "" : "33"}`,
                color: isActivePhase ? "#FFFFFF" : seg.color,
              }}
              data-testid={`phase-band-${seg.id}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap">
                {seg.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status, brandBlue }) {
  const colors = {
    idle:    { bg: "#F1F5F9", fg: "#64748B" },
    running: { bg: "#FEF3C7", fg: "#B45309" },
    ready:   { bg: "#DBEAFE", fg: brandBlue },
    passed:  { bg: "#ECFDF5", fg: "#0F766E" },
  };
  const c = colors[status] || colors.idle;
  return (
    <span
      className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {status === "running" && (
        <span className="inline-block w-1 h-1 rounded-full mr-1 animate-pulse" style={{ backgroundColor: c.fg }} />
      )}
      {STATUS_LABELS[status]}
    </span>
  );
}

// ==================== Agent Workbench ====================
function AgentWorkbench({ hop, agentState, stageOverrides, onOverride, onClearOverrides, onSendHandoff, onRun, onConnectSources, onRunReasoning, hasPlanPhase, onOpenDocs, docCount, forecastLink, planPanel, brandBlue, brandSoft }) {
  const c = hop.console || {};
  const s = agentState || emptyAgent();
  const [secondaryTab, setSecondaryTab] = useState("what");
  // Editable when the agent is not actively running or already committed
  // (idle / ready). This lets presenters iterate what-ifs without a full reset.
  const canEdit = s.status !== "running" && s.status !== "passed";
  const editableIndices = EDITABLE_INBOX[hop.stage] || [];
  const hasOverrides = Object.keys(stageOverrides).length > 0;

  // Recompute the decision from overrides. Only applied for agents whose
  // editable inbox indices map directly to decision terms (currently just
  // Demand — 4 delta rows correspond to 4 decision terms).
  const effectiveDecision = useMemo(() => {
    if (!c.decision) return c.decision;
    if (!hasOverrides) return c.decision;
    const terms = c.decision.terms.map((t, i) => {
      const v = stageOverrides[i];
      return v == null ? t : { ...t, value: Number(v) };
    });
    const total = terms.reduce((acc, t) => acc + (t.sign === "−" ? -Number(t.value) : Number(t.value)), 0);
    return { ...c.decision, terms, total_value: total, equation: buildEquation(terms, total) };
  }, [c.decision, stageOverrides, hasOverrides]);

  // Build a display value + delta string for an inbox row given any override.
  const inboxRow = (row, i) => {
    if (!editableIndices.includes(i)) return row;
    const v = stageOverrides[i];
    if (v == null) return row;
    const term = c.decision?.terms?.[i];
    const sign = term?.sign === "−" ? "-" : "+";
    return {
      ...row,
      delta: `${sign}${v}`,
      field: row.field.replace(/[+\-]?\d[\d,]*/, `${sign}${v}`),
    };
  };

  // Build a rewritten "Reconcile" reasoning step when overrides are active.
  const effectiveReasoning = useMemo(() => {
    if (!c.reasoning) return [];
    if (!hasOverrides || !effectiveDecision) return c.reasoning;
    // Find the last step that contains the original equation; rewrite it.
    const rewritten = [...c.reasoning];
    const idxReconcile = rewritten.findIndex((s) => /Reconcile:/i.test(s));
    if (idxReconcile >= 0) {
      rewritten[idxReconcile] = `Reconcile: ${effectiveDecision.equation} ${effectiveDecision.total_label.toLowerCase()}.`;
    }
    return rewritten;
  }, [c.reasoning, hasOverrides, effectiveDecision]);

  // Rewrite the handoff `required` field to reflect the new total.
  const effectiveHandoff = useMemo(() => {
    if (!hasOverrides || !c.handoff || !effectiveDecision) return c.handoff;
    const fields = c.handoff.fields.map((f) =>
      f.key === "required" ? { ...f, value: String(effectiveDecision.total_value) } : f
    );
    return { ...c.handoff, fields };
  }, [c.handoff, hasOverrides, effectiveDecision]);

  return (
    <div
      className="rounded-2xl bg-white p-6 md:p-8 mb-10"
      style={{ border: `1px solid ${brandSoft}`, boxShadow: `0 1px 0 ${brandSoft}` }}
      data-testid={`workbench-${hop.stage}`}
    >
      {/* Workbench header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-1 flex items-center gap-2" style={{ color: brandBlue }}>
            <span>{hop.name}</span>
            {hasOverrides && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}
                data-testid="custom-inputs-pill"
              >
                <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: "#B45309" }} />
                Custom inputs applied
              </span>
            )}
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">{hop.agent}</h2>
          <p className="text-slate-500 mt-1.5 text-[14px]">{hop.principle}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasOverrides && (
            <Button
              data-testid="reset-inputs-btn"
              variant="outline"
              onClick={onClearOverrides}
              className="h-9 px-3 border-slate-200 text-xs"
            >
              <RotateCcw size={11} className="mr-1.5" />
              Reset inputs
            </Button>
          )}
          {/* Top-level Run agent button — hidden for Plan-equipped stages
              (Refresh / Demand / Recovery) because they use split triggers:
              "Connect sources" in Collect + "Run agent" in Plan. */}
          {s.status !== "running" && !hasPlanPhase && (
            <Button
              data-testid={`workbench-run-btn-${hop.stage}`}
              variant="outline"
              onClick={onRun}
              className="h-9 px-3 border-slate-200"
            >
              <Play size={12} className="mr-1.5" style={{ color: brandBlue }} />
              {(s.status === "passed" || s.status === "ready") ? "Replay" : "Run agent"}
            </Button>
          )}
        </div>
      </div>

      {/* ========================================================= */}
      {/* 5-phase vertical flow: Reading inputs → Resolved → Reasoning → Decision → Handoff */}
      {/* ========================================================= */}
      <AgentPhaseFlow
        hop={hop}
        s={s}
        c={c}
        effectiveDecision={effectiveDecision}
        effectiveReasoning={effectiveReasoning}
        effectiveHandoff={effectiveHandoff}
        canEdit={canEdit}
        editableIndices={editableIndices}
        stageOverrides={stageOverrides}
        hasOverrides={hasOverrides}
        onOverride={onOverride}
        onSendHandoff={onSendHandoff}
        onOpenDocs={onOpenDocs}
        docCount={docCount}
        forecastLink={forecastLink}
        planPanel={planPanel}
        hasPlanPhase={hasPlanPhase}
        onConnectSources={onConnectSources}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
        running={s.status === "running"}
        settled={s.decisionSettled}
      />

      {/* Device Intelligence · inline DEX Risk Cohort (gated until agent runs) */}
      {hop.stage === "operate" && (
        <div className="mt-4">
          <DexRiskCohort
            unlocked={s.status === "running" || s.status === "ready" || s.status === "passed" || s.decisionSettled}
            brandBlue={brandBlue}
            brandSoft={brandSoft}
          />
        </div>
      )}

      {/* Secondary tabs */}
      <div className="mt-8 pt-6 border-t border-slate-100">
        <div className="flex items-center gap-1 mb-4">
          {[
            { id: "what",         label: "What it does" },
            { id: "integrations", label: "Real integrations tapped" },
            { id: "value",        label: "Value line" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`secondary-tab-${t.id}`}
              onClick={() => setSecondaryTab(t.id)}
              className="text-xs font-semibold h-8 px-3 rounded-md transition-colors"
              style={
                secondaryTab === t.id
                  ? { backgroundColor: brandSoft, color: brandBlue }
                  : { color: "#64748B" }
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        {secondaryTab === "what" && (
          <div className="text-sm text-slate-700 leading-6 max-w-3xl">
            <span className="font-semibold text-slate-900">{hop.framing}</span>
            {" · "}
            {hop.flexspace_intelligence}
          </div>
        )}
        {secondaryTab === "integrations" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 max-w-3xl">
            {(hop.integrations || []).map((it) => (
              <div key={it.name} className="text-sm flex items-center gap-2">
                <span className="font-semibold text-slate-900 shrink-0">{it.name}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-600">{it.role}</span>
              </div>
            ))}
          </div>
        )}
        {secondaryTab === "value" && (
          <div className="text-sm text-slate-700 leading-6 max-w-3xl">
            {hop.demo_line}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Panel container ====================
function Panel({ title, subtitle, className = "", testid, children }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">{title}</div>
        {subtitle && <div className="text-[10px] text-slate-400">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ==================== Decision Panel ====================
function DecisionPanel({ decision, termsRevealed, settled, forecastLink, brandBlue }) {
  // Hooks first — React requires them before any early return.
  const [pulseKey, setPulseKey] = useState(0);
  const prevP50 = useRef(null);
  useEffect(() => {
    if (!forecastLink) return;
    if (prevP50.current !== null && prevP50.current !== forecastLink.p50) {
      setPulseKey((k) => k + 1);
    }
    prevP50.current = forecastLink.p50;
  }, [forecastLink?.p50]);

  if (!decision) return <div className="text-xs text-slate-400">No decision configured.</div>;
  const terms = decision.terms || [];
  const revealed = terms.slice(0, termsRevealed);

  return (
    <div>
      {forecastLink && (
        <div
          key={pulseKey}
          className="mb-3 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap"
          style={{
            border: `1px solid ${brandBlue}33`,
            backgroundColor: `${brandBlue}0A`,
            animation: pulseKey > 0 ? "forecastPulse 650ms ease-out" : undefined,
          }}
          data-testid="decision-forecast-link"
        >
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brandBlue }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>
              Linked to forecast above
            </span>
          </div>
          <span className="text-[11px] font-mono text-slate-700">
            <span className="font-bold" style={{ color: brandBlue }}>P50 {forecastLink.p50}</span>
            <span className="text-slate-400 mx-1.5">·</span>
            P10 {forecastLink.p10}
            <span className="text-slate-400 mx-1.5">·</span>
            P90 {forecastLink.p90}
            <span className="text-slate-400 mx-1.5">·</span>
            {forecastLink.confidence}% conf
          </span>
          <span className="text-[10px] text-slate-500 italic ml-auto">drag a slider above · this strip pulses</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3 font-mono text-[13px]">
        {revealed.map((t, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 transition-all"
            style={{ animation: "slideIn 300ms ease-out both" }}
          >
            {i > 0 && <span className="text-slate-400">{t.sign === "•" ? "→" : t.sign}</span>}
            {i === 0 && t.sign === "−" && <span className="text-slate-400">−</span>}
            <span className="font-semibold text-slate-900">{t.value.toLocaleString()}</span>
            <span className="text-[11px] text-slate-500">{t.label}</span>
          </span>
        ))}
        {settled && (
          <>
            <span className="text-slate-400">=</span>
            <span
              key={`total-${pulseKey}`}
              className="text-2xl font-bold"
              style={{
                color: brandBlue,
                animation: pulseKey > 0
                  ? "settleIn 500ms ease-out both, forecastPulse 650ms ease-out"
                  : "settleIn 500ms ease-out both",
              }}
              data-testid="decision-total"
            >
              {decision.total_value.toLocaleString()}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {decision.total_label}
            </span>
          </>
        )}
      </div>
      {settled && decision.sub_breakdown && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
          {decision.sub_breakdown.map((b) => (
            <div key={b.label}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{b.label}</div>
              <div className="text-sm font-bold text-slate-900">{b.value}</div>
            </div>
          ))}
        </div>
      )}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes settleIn {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes forecastPulse {
          0%   { box-shadow: 0 0 0 0   ${brandBlue}66; }
          70%  { box-shadow: 0 0 0 10px ${brandBlue}00; }
          100% { box-shadow: 0 0 0 0   ${brandBlue}00; }
        }
      `}</style>
    </div>
  );
}

// ==================== Reasoning Panel ====================
function ReasoningPanel({ steps, revealed, running, brandBlue }) {
  return (
    <div className="font-mono text-[12px] leading-6 text-slate-700 min-h-[120px]">
      {steps.slice(0, revealed).map((step, i) => (
        <div key={i} className="flex gap-2 py-0.5" style={{ animation: "fadeInUp 260ms ease-out both" }}>
          <span className="text-slate-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
          <span>{step}</span>
        </div>
      ))}
      {running && revealed < steps.length && (
        <div className="flex gap-2 py-0.5 items-center">
          <span className="text-slate-400 shrink-0">{String(revealed + 1).padStart(2, "0")}</span>
          <span className="inline-block w-1.5 h-3 animate-pulse" style={{ backgroundColor: brandBlue }} />
        </div>
      )}
      {revealed === 0 && !running && (
        <div className="text-slate-400 italic">Idle · press Run to trace this agent's reasoning</div>
      )}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ==================== Outbox Panel ====================
function OutboxPanel({ handoff, revealed, sent, onSend, status, brandBlue, brandSoft }) {
  if (!handoff) return <div className="text-xs text-slate-400">No handoff configured.</div>;
  const fields = handoff.fields || [];
  const canSend = status === "ready" && !sent && revealed >= fields.length;
  return (
    <div>
      <div
        className="rounded-lg font-mono text-[11px] p-3 mb-3"
        style={{
          backgroundColor: "#0F172A",
          color: "#E2E8F0",
          minHeight: 120,
          transition: "transform 500ms ease-out",
          transform: sent ? "translateX(30px)" : "translateX(0)",
          opacity: sent ? 0.4 : 1,
        }}
      >
        <div className="text-slate-400">{"{"}</div>
        {fields.slice(0, revealed).map((f) => (
          <div key={f.key} className="pl-3 flex" style={{ animation: "fadeInUp 250ms ease-out both" }}>
            <span style={{ color: "#7DD3FC" }}>&quot;{f.key}&quot;</span>
            <span className="text-slate-500">:&nbsp;</span>
            <span style={{ color: "#FDE68A" }}>&quot;{String(f.value)}&quot;</span>
            <span className="text-slate-500">,</span>
          </div>
        ))}
        <div className="text-slate-400">{"}"}</div>
      </div>
      {sent ? (
        <div
          className="rounded-md p-2 flex items-center gap-2 text-[11px] font-semibold"
          style={{ backgroundColor: "#ECFDF5", color: "#0F766E" }}
          data-testid="handoff-sent"
        >
          <CheckCircle2 size={13} />
          Handoff committed to {handoff.target_label}
          {handoff.closes_loop && <span className="ml-1 text-[10px] opacity-80">· closes the loop</span>}
        </div>
      ) : (
        <Button
          data-testid="send-handoff-btn"
          disabled={!canSend}
          onClick={onSend}
          className="w-full h-9 text-white hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: brandBlue }}
        >
          <Send size={12} className="mr-1.5" />
          Send handoff to {handoff.target_label}
        </Button>
      )}
    </div>
  );
}

// ==================== State helpers ====================
function emptyAgent() {
  return {
    status: "idle",
    inboxRevealed: 0,
    reasoningRevealed: 0,
    decisionTermsRevealed: 0,
    decisionSettled: false,
    outboxRevealed: 0,
    handoffSent: false,
    collectReady: false, // Phase 1 done — enables Run-agent on Plan step
  };
}

function makeInitialState() {
  return {
    plan:    emptyAgent(),
    source:  emptyAgent(),
    deploy:  emptyAgent(),
    operate: emptyAgent(),
    refresh: emptyAgent(),
    recover: emptyAgent(),
  };
}


// ==================== Editable Inbox Row ====================
function EditableInboxRow({ row, value, onChange, brandBlue }) {
  return (
    <div className="mt-1">
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
          className="w-20 text-[15px] font-bold rounded-md border border-amber-300 bg-white px-2 py-1 outline-none focus:ring-2"
          style={{ color: "#0F172A" }}
          data-testid={`inbox-input-${row.system.toLowerCase().replace(/\W+/g, "-")}`}
        />
        <div className="text-[11px] font-semibold text-slate-700 truncate">
          {row.field.replace(/[+\-]?\d[\d,]*\s*/, "").trim() || "value"}
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{row.detail}</div>
    </div>
  );
}

// ==================== Equation builder ====================
function buildEquation(terms, total) {
  // Produce a math-style equation such as "620 + 186 − 34 − 54 = 718".
  const parts = terms.map((t, i) => {
    const abs = Math.abs(Number(t.value)).toLocaleString();
    if (i === 0) return t.sign === "−" ? `− ${abs}` : abs;
    return t.sign === "−" ? `− ${abs}` : `+ ${abs}`;
  });
  return `${parts.join(" ")} = ${total.toLocaleString()}`;
}

// ==================== Story Hero Band ====================
// Sits at the top of /lifecycle above the six-agent rail. Sets the scene
// with the executive question + a compact preview of the accumulated
// Lifecycle Context (six curated keys from the story payload).
function StoryHeroBand({ story, brandBlue, brandSoft }) {
  if (!story?.scenario) return null;
  const context = { ...(story.context_seed || {}) };
  for (const h of story.hops || []) Object.assign(context, h.context_added || {});
  const HERO_KEYS = [
    "net_requirement", "sourcing_plan", "day1_ready",
    "hotspot_cohort", "refresh_decisions", "returns_pipeline_next_qtr",
  ];
  const entries = HERO_KEYS.filter((k) => context[k]).map((k) => [k, context[k]]);
  return (
    <div
      className="rounded-2xl p-5 md:p-6 relative overflow-hidden mb-6"
      style={{ background: `linear-gradient(135deg, ${brandSoft} 0%, #ffffff 60%)`, border: `1px solid ${brandSoft}` }}
      data-testid="lifecycle-story-hero"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap mb-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold mb-2" style={{ color: brandBlue }}>
            The lifecycle in one glance
          </div>
          <div className="flex items-start gap-2">
            <Quote size={16} className="mt-0.5 shrink-0" style={{ color: brandBlue }} />
            <p className="text-[15px] leading-6 font-medium text-slate-900 max-w-3xl">
              {story.scenario.opening_question}
            </p>
          </div>
        </div>
      </div>
      <div className="border-t pt-3" style={{ borderColor: brandSoft }}>
        <div className="flex flex-wrap gap-1.5">
          {entries.map(([k, v]) => (
            <span key={k} className="text-[10px] font-medium bg-white/70 rounded-md px-2 py-1" style={{ color: "#0F172A", border: `1px solid ${brandBlue}22` }}>
              <span className="text-slate-500 font-semibold uppercase tracking-wider text-[9px] mr-1">
                {k.replace(/_/g, " ")}:
              </span>
              <span style={{ color: brandBlue }}>{String(v).length > 42 ? String(v).slice(0, 42) + "…" : String(v)}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


// ==================== DEX Risk Cohort (Device agent · inline fifth panel) ====================
// Renders the 73-flagged / 386-monitored cohort below the four-panel workbench
// when the active agent is Device Intelligence. Uses the same <DexDeviceRow>
// as the Cockpit's DexRiskDrawer so expansion / sparkline / actions behave
// identically on both surfaces.
function DexRiskCohort({ unlocked, brandBlue, brandSoft }) {
  const [cohort, setCohort] = useState(null);
  const [filter, setFilter] = useState("flagged"); // flagged | all
  const [expandedId, setExpandedId] = useState(null);

  // Only pull the 386-device cohort once the Device Intelligence agent has
  // actually started running — stops the panel from pre-populating evidence
  // before the executive has clicked "Run agent".
  useEffect(() => {
    if (!unlocked || cohort) return;
    endpoints.lifecycleDexCohort().then(setCohort).catch(() => toast.error("Could not load DEX cohort."));
  }, [unlocked, cohort]);

  if (!unlocked) {
    return (
      <div
        className="rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center gap-2 bg-slate-50/40"
        style={{ borderColor: `${brandBlue}44` }}
        data-testid="dex-cohort-locked"
      >
        <div
          className="w-10 h-10 rounded-full grid place-items-center"
          style={{ backgroundColor: `${brandBlue}14`, color: brandBlue }}
        >
          <Zap size={16} />
        </div>
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">
          DEX Risk Cohort · evidence rail
        </div>
        <div className="text-[12px] text-slate-600 max-w-md">
          Run the <span className="font-semibold text-slate-900">Device Intelligence agent</span> above to populate the 386-device cohort and surface the 73 flagged units.
        </div>
      </div>
    );
  }

  const list = cohort?.list || [];
  const filtered = filter === "flagged" ? list.filter((d) => d.flagged) : list;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="dex-cohort-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">
            DEX Risk Cohort · evidence rail
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {cohort ? `${cohort.total} monitored · ${cohort.flagged} flagged · avg DEX ${cohort.avg_dex} · ${cohort.cohort_label}` : "Loading…"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[
            { id: "all",     label: `All (${list.length})` },
            { id: "flagged", label: `Flagged (${list.filter((d) => d.flagged).length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className="text-[11px] font-semibold h-7 px-2.5 rounded-md transition-colors"
              style={
                filter === f.id
                  ? { backgroundColor: brandBlue, color: "#fff" }
                  : { color: "#64748B", border: "1px solid #E2E8F0" }
              }
              data-testid={`dex-cohort-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!cohort ? (
        <div className="text-xs text-slate-400 py-4 text-center">Loading cohort…</div>
      ) : (
        <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
          {filtered.map((d) => (
            <DexDeviceRow
              key={d.device_id}
              device={d}
              expanded={expandedId === d.device_id}
              onToggle={() => setExpandedId(expandedId === d.device_id ? null : d.device_id)}
              brandBlue={brandBlue}
              brandSoft={brandSoft}
            />
          ))}
        </div>
      )}
    </div>
  );
}
